import {
  Box,
  ButtonBase,
  LinearProgress,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import {
  parseLocalDateParam,
  PeriodStepper,
} from "components/common/PeriodStepper";
import dayjs, { Dayjs } from "dayjs";
import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";
import { useTenant } from "hooks/settings";
import { useLocalDatetime } from "hooks/useLocalDateTime";
import { useCallback, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router";
import {
  DiffMode,
  RecipeComplianceReport as RecipeComplianceReportResponse,
  useGetRecipeComplianceReportDailyQuery,
  useGetRecipeComplianceReportMonthlyQuery,
} from "src/store/api/generatedApi";
import { RecipeComplianceEmptyState } from "./RecipeComplianceEmptyState";
import { RecipeComplianceReport } from "./RecipeComplianceReport";

type ReportPeriod = "daily" | "monthly";
interface SegmentedControlOption<Value extends string> {
  value: Value;
  label: string;
}

const PERIOD_OPTIONS: readonly ReportPeriod[] = ["daily", "monthly"] as const;
const DIFF_MODE_OPTIONS: readonly DiffMode[] = ["mass", "fraction"] as const;
const REPORT_QUERY_PARAMS = {
  date: "date",
  monthDate: "monthDate",
  diffMode: "diffMode",
  timeAggregation: "timeAggregation",
} as const;
const LEGACY_REPORT_QUERY_PARAMS = [
  "recipeComplianceAnchorDate",
  "recipeComplianceDiffMode",
  "recipeCompliancePeriod",
] as const;
const REPORT_MAX_WIDTH_PX = 1520;

const isPeriod = (value: string | null): value is ReportPeriod =>
  PERIOD_OPTIONS.includes(value as ReportPeriod);

const isDiffMode = (value: string | null): value is DiffMode =>
  DIFF_MODE_OPTIONS.includes(value as DiffMode);

export const RecipeComplianceReportTab = () => {
  const { t } = useTenantTranslation();
  const tenantName = useTenant();
  const { localTimezone } = useLocalDatetime();
  const [searchParams, setSearchParams] = useSearchParams();

  const updateReportQueryParams = useCallback(
    (updates: Partial<Record<keyof typeof REPORT_QUERY_PARAMS, string>>) => {
      setSearchParams(
        (currentParams) => {
          const nextParams = new URLSearchParams(currentParams);
          LEGACY_REPORT_QUERY_PARAMS.forEach((param) => {
            nextParams.delete(param);
          });
          Object.entries(updates).forEach(([key, value]) => {
            nextParams.set(
              REPORT_QUERY_PARAMS[key as keyof typeof REPORT_QUERY_PARAMS],
              value
            );
          });
          return nextParams;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const periodParam =
    searchParams.get(REPORT_QUERY_PARAMS.timeAggregation) ??
    searchParams.get("recipeCompliancePeriod");
  const period: ReportPeriod = isPeriod(periodParam) ? periodParam : "daily";

  const rawAnchorDateParam =
    searchParams.get(REPORT_QUERY_PARAMS.date) ??
    searchParams.get("recipeComplianceAnchorDate");
  const anchorDateParam = rawAnchorDateParam?.trim() ?? null;
  const anchorDate: Dayjs = useMemo(
    () =>
      parseLocalDateParam(
        anchorDateParam,
        localTimezone,
        dayjs().tz(localTimezone)
      ),
    [anchorDateParam, localTimezone]
  );

  const monthDateParam =
    searchParams.get(REPORT_QUERY_PARAMS.monthDate)?.trim() ?? null;
  const monthAnchorDate: Dayjs = useMemo(
    () =>
      parseLocalDateParam(monthDateParam, localTimezone, anchorDate).startOf(
        "month"
      ),
    [anchorDate, localTimezone, monthDateParam]
  );

  const diffModeParam =
    searchParams.get(REPORT_QUERY_PARAMS.diffMode) ??
    searchParams.get("recipeComplianceDiffMode");
  const userPickedDiffMode: DiffMode | null = isDiffMode(diffModeParam)
    ? diffModeParam
    : null;

  const anchorDateString = anchorDate.format("YYYY-MM-DD");
  const monthAnchorDateString = monthAnchorDate.format("YYYY-MM-DD");
  const selectedDate = period === "monthly" ? monthAnchorDate : anchorDate;
  const currentDate = useMemo(() => dayjs().tz(localTimezone), [localTimezone]);
  const maxSelectableDate =
    period === "monthly" ? currentDate.startOf("month") : currentDate;
  // Dashboard daily views are calendar-day anchored. diffMode is not a query
  // arg: the breakdown carries both mass and fraction, so the toggle re-renders
  // from cache rather than refetching.
  const dailyQuery = useGetRecipeComplianceReportDailyQuery(
    {
      tenantName,
      anchorDate: anchorDateString,
      shiftAnchor: "first",
    },
    { skip: period !== "daily" }
  );
  const monthlyQuery = useGetRecipeComplianceReportMonthlyQuery(
    {
      tenantName,
      anchorDate: monthAnchorDateString,
    },
    { skip: period !== "monthly" }
  );
  const { data, isFetching, isError } =
    period === "daily" ? dailyQuery : monthlyQuery;

  const diffMode: DiffMode =
    userPickedDiffMode ?? data?.default_diff_mode ?? "mass";

  useEffect(() => {
    const updates: Partial<Record<keyof typeof REPORT_QUERY_PARAMS, string>> =
      {};
    if (periodParam !== period) {
      updates.timeAggregation = period;
    }
    if (period === "daily" && anchorDateParam !== anchorDateString) {
      updates.date = anchorDateString;
    }
    if (period === "monthly" && monthDateParam !== monthAnchorDateString) {
      updates.monthDate = monthAnchorDateString;
    }
    if (!isDiffMode(diffModeParam) && data?.default_diff_mode !== undefined) {
      updates.diffMode = data.default_diff_mode;
    }
    if (Object.keys(updates).length > 0) {
      updateReportQueryParams(updates);
    }
  }, [
    anchorDateParam,
    anchorDateString,
    data?.default_diff_mode,
    diffModeParam,
    monthAnchorDateString,
    monthDateParam,
    period,
    periodParam,
    updateReportQueryParams,
  ]);

  const setReportDate = useCallback(
    (date: Dayjs) => {
      const localDate = date.tz(localTimezone, true);
      updateReportQueryParams(
        period === "monthly"
          ? { monthDate: localDate.startOf("month").format("YYYY-MM-DD") }
          : { date: localDate.format("YYYY-MM-DD") }
      );
    },
    [localTimezone, period, updateReportQueryParams]
  );

  return (
    <Stack spacing={1.25} sx={{ height: "100%", pt: 1 }}>
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
        <SegmentedControl
          ariaLabel={t("timeAggregation")}
          value={period}
          options={[
            { value: "daily", label: t("daily") },
            { value: "monthly", label: t("monthly") },
          ]}
          onChange={(value) => {
            updateReportQueryParams({ timeAggregation: value });
          }}
        />
        <PeriodStepper
          value={selectedDate}
          onChange={setReportDate}
          unit={period === "monthly" ? "month" : "day"}
          maxDate={maxSelectableDate}
        />
        <SegmentedControl
          ariaLabel={`${t("mass")} / ${t("percent")}`}
          value={diffMode}
          options={[
            { value: "mass", label: t("mass") },
            { value: "fraction", label: t("percent") },
          ]}
          onChange={(value) => {
            updateReportQueryParams({ diffMode: value });
          }}
        />
      </Stack>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          position: "relative",
          width: "100%",
        }}
      >
        {isFetching && (
          <LinearProgress
            sx={{
              position: "sticky",
              top: 0,
              left: 0,
              right: 0,
              zIndex: 1,
            }}
          />
        )}
        <ReportBody
          data={data}
          diffMode={diffMode}
          period={period}
          isFetching={isFetching}
          isError={isError}
        />
      </Box>
    </Stack>
  );
};

const SegmentedControl = <Value extends string>({
  ariaLabel,
  value,
  options,
  onChange,
}: {
  ariaLabel: string;
  value: Value;
  options: readonly SegmentedControlOption<Value>[];
  onChange: (value: Value) => void;
}) => (
  <Box
    aria-label={ariaLabel}
    role="radiogroup"
    sx={{
      alignItems: "center",
      bgcolor: "grey.50",
      border: 1,
      borderColor: "grey.300",
      borderRadius: 1,
      display: "inline-flex",
      gap: 0.25,
      p: 0.25,
    }}
  >
    {options.map((option) => {
      const optionIndex = options.findIndex(
        ({ value }) => value === option.value
      );
      const isSelected = option.value === value;
      return (
        <ButtonBase
          key={option.value}
          aria-checked={isSelected}
          onClick={() => {
            onChange(option.value);
          }}
          onKeyDown={(event) => {
            const nextIndexByKey: Record<string, number> = {
              ArrowDown: (optionIndex + 1) % options.length,
              ArrowRight: (optionIndex + 1) % options.length,
              ArrowLeft: (optionIndex - 1 + options.length) % options.length,
              ArrowUp: (optionIndex - 1 + options.length) % options.length,
              End: options.length - 1,
              Home: 0,
            };
            const nextIndex = nextIndexByKey[event.key];
            if (nextIndex === undefined) return;
            const nextOption = options[nextIndex];
            if (nextOption === undefined) return;
            event.preventDefault();
            onChange(nextOption.value);
          }}
          role="radio"
          type="button"
          sx={{
            bgcolor: isSelected ? "primary.main" : "transparent",
            borderRadius: 0.75,
            color: isSelected ? "primary.contrastText" : "text.secondary",
            fontFamily: (theme) => theme.typography.fontFamily,
            fontSize: 14,
            fontWeight: 600,
            lineHeight: 1.4,
            minHeight: 32,
            minWidth: 82,
            px: 1.75,
            transition:
              "background-color 120ms ease, box-shadow 120ms ease, color 120ms ease",
            whiteSpace: "nowrap",
            "&:hover": {
              bgcolor: isSelected ? "primary.dark" : "action.hover",
              color: isSelected ? "primary.contrastText" : "text.primary",
            },
            "&:focus-visible": {
              outline: 2,
              outlineColor: "primary.main",
              outlineOffset: 2,
            },
          }}
        >
          {option.label}
        </ButtonBase>
      );
    })}
  </Box>
);

const ReportBody = ({
  data,
  diffMode,
  period,
  isFetching,
  isError,
}: {
  data: RecipeComplianceReportResponse | undefined;
  diffMode: DiffMode;
  period: ReportPeriod;
  isFetching: boolean;
  isError: boolean;
}) => {
  const { t } = useTenantTranslation();
  const units = useUnitsFormatter(true);
  if (isError) {
    return <Typography color="error">{t("failedToLoadReport")}</Typography>;
  }
  if (data === undefined) {
    return isFetching ? <Skeleton variant="rectangular" height="100%" /> : null;
  }
  if (!data.breakdown) {
    return (
      <RecipeComplianceEmptyState
        title={data.title}
        period={period}
        windowStart={data.window_start}
      />
    );
  }
  return (
    <Box sx={{ maxWidth: REPORT_MAX_WIDTH_PX, mx: "auto" }}>
      <RecipeComplianceReport
        breakdown={data.breakdown}
        diffMode={diffMode}
        variant="interactive"
        t={t}
        units={units}
      />
    </Box>
  );
};
